import { useEffect, useRef } from 'react';
import { ThreadCard } from './ThreadCard';
import { EmptyState } from './EmptyState';
import { Loader2 } from 'lucide-react';
import { ThreadListItemDto, ThreadListQuery } from '../../types';
import { useThreads } from '../../hooks/useConversations';

interface ThreadListProps {
  filters?: ThreadListQuery;
  selectedThreadId?: string;
  onThreadSelect?: (threadId: string, senderId: string) => void;
}

export function ThreadList({
  filters,
  selectedThreadId,
  onThreadSelect,
}: ThreadListProps) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useThreads(filters);

  const observerTarget = useRef<HTMLDivElement>(null);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten pages into single array
  const threads = data?.pages.flatMap((page) => page.items) ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (threads.length === 0) {
    return <EmptyState message="Nenhuma conversa encontrada" />;
  }

  return (
    <div className="space-y-2">
      {threads.map((thread: ThreadListItemDto) => (
        <ThreadCard
          key={thread.id}
          thread={thread}
          isSelected={thread.id === selectedThreadId}
          onClick={() => onThreadSelect?.(thread.id, thread.senderId)}
        />
      ))}

      {/* Intersection observer target */}
      <div ref={observerTarget} className="h-4" />

      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* No more data indicator */}
      {!hasNextPage && threads.length > 0 && (
        <div className="text-center text-sm text-muted-foreground py-4">
          Fim da lista
        </div>
      )}
    </div>
  );
}
